"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useStoreStore } from "@/stores/store-store";
import { useAuthStore, switchActiveStore } from "@/stores/auth-store";
import { hasPermission } from "@/lib/rbac";
import { ChevronDown, Store, Globe, Check } from "lucide-react";

/**
 * Warna lencana per tier.
 *
 * SUBSC-33 mengubah `tier` supaya diturunkan dari `plan_id` alih-alih dibaca
 * dari kolom `stores.tier` yang bisa basi. Efek sampingnya di sisi kita: set
 * nilainya berubah jadi `trial | starter | business | corporate`, dan
 * **`"trial"` akan muncul untuk pertama kalinya dalam sejarah sistem ini** —
 * kolom lama punya DEFAULT `'startup'`, jadi store trial tidak pernah
 * mengirim `"trial"` sekali pun.
 *
 * `startup` dipertahankan sementara: selama peluncuran, sebagian store masih
 * bisa mengembalikannya. Ia boleh dihapus sesudah SUBSC-33 hidup di produksi.
 */
const TIER_COLORS: Record<string, string> = {
  trial: "bg-amber-100 text-amber-700",
  starter: "bg-gray-100 text-gray-700",
  startup: "bg-gray-100 text-gray-700",
  business: "bg-blue-100 text-blue-700",
  corporate: "bg-purple-100 text-purple-700",
};

/**
 * Nilai tier yang tidak dikenal tetap dapat gaya, bukan `undefined`.
 *
 * Tanpa ini, `TIER_COLORS[tier]` untuk nilai baru menghasilkan `undefined`
 * yang ikut ter-interpolasi ke `className` sebagai teks "undefined" — lencana
 * tanpa gaya, tanpa galat, tanpa test yang gagal. Cuma terlihat rusak.
 *
 * Pola ini sudah dipakai di tempat lain di repo ini (`STATUS_STYLE`,
 * `CATEGORY_COLORS`, `TIER_STYLE` di waba-phone-panel); di sini yang terlewat.
 */
const TIER_COLOR_DEFAULT = "bg-gray-100 text-gray-600";

export function StoreSelector({ collapsed = false }: { collapsed?: boolean }) {
  const t = useTranslations("store");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const user = useAuthStore((s) => s.user);
  const { stores, activeStoreId, isGlobalView, setGlobalView } = useStoreStore();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  // Only users with multiple stores or global view permission see this
  const canGlobalView = hasPermission(user.role, "store.global_view");
  const userStores = stores.filter((s) => user.stores.some((us) => us.storeId === s.id));

  if (userStores.length <= 1 && !canGlobalView) return null;

  const activeStore = stores.find((s) => s.id === activeStoreId);
  // BILL-03 — `store.allStores` dan `store.selectStore` sudah ada di kedua
  // katalog sejak lama; yang tidak pernah terjadi cuma menyambungkannya.
  const displayName = isGlobalView ? t("allStores") : activeStore?.name || t("selectStore");

  if (collapsed) {
    return (
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-center px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
        title={displayName}
      >
        {isGlobalView ? <Globe className="w-5 h-5 text-blue-600" /> : <Store className="w-5 h-5 text-gray-500" />}
      </button>
    );
  }

  return (
    <div ref={ref} className="relative px-3 mb-2">
      <button
        data-testid="store-selector-btn"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
      >
        {isGlobalView ? (
          <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
        ) : (
          <Store className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
          {/* BILL-03 — nama paket datang dari `plans.name`, bukan dari slug.
              Versi lama merender `{tier} Plan` dengan `capitalize` di CSS:
              "starter" jadi "Starter Plan" — kebetulan benar selama nama paket
              satu kata dan sama dengan slug-nya. Paket bernama "Business Pro"
              (slug `business-pro`) akan tampil "Business-pro Plan".

              Tanpa nama paket tidak ada yang dirender, mengikuti SUBSC-34:
              bukan tier sebagai pengganti, bukan default berbayar. */}
          {!isGlobalView && activeStore?.planName && (
            <p data-testid="store-selector-plan" className="text-[10px] text-gray-500">
              {activeStore.planName}
            </p>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="store-selector-menu"
          className="absolute left-3 right-3 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-[300px] overflow-y-auto"
        >
          {/* Global View */}
          {canGlobalView && (
            <>
              <button
                data-testid="store-option-global"
                data-active={isGlobalView}
                onClick={() => {
                  setGlobalView(true);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                  isGlobalView ? "bg-blue-50 text-blue-700" : "text-gray-700"
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="flex-1 text-left font-medium">Semua Toko</span>
                {isGlobalView && <Check className="w-4 h-4 text-blue-600" />}
              </button>
              <div className="border-t border-gray-100 my-1" />
            </>
          )}

          {/* Store List */}
          {userStores.map((store) => (
            <button
              key={store.id}
              data-testid={`store-option-${store.id}`}
              data-active={!isGlobalView && store.id === activeStoreId}
              onClick={() => {
                switchActiveStore(store.id);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                !isGlobalView && store.id === activeStoreId ? "bg-blue-50 text-blue-700" : "text-gray-700"
              }`}
            >
              <Store className="w-4 h-4 flex-shrink-0" />
              <div className="flex-1 text-left min-w-0">
                <p className="font-medium truncate">{store.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {/* BILL-03 — WARNANYA dari tier, TEKSNYA dari nama paket.
                      Keduanya pemakaian slug yang berbeda: sebagai kunci ke
                      peta gaya ia benar dan tetap; sebagai teks yang dibaca
                      orang ia salah. Lencana tanpa nama paket tidak dirender
                      sama sekali — bukan slug sebagai penggantinya. */}
                  {store.planName && (
                    <span
                      data-testid={`store-option-plan-${store.id}`}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${TIER_COLORS[store.tier] ?? TIER_COLOR_DEFAULT}`}
                    >
                      {store.planName}
                    </span>
                  )}
                  {store.status === "trial" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium">
                      {t("trial")}
                    </span>
                  )}
                </div>
              </div>
              {!isGlobalView && store.id === activeStoreId && <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
